import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Upload,
  Loader2,
  Fuel,
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
  Pencil,
  Trash2,
  ImagePlus,
  Clock,
  X,
  ChevronDown,
  Truck,
  Gauge,
  Target,
  Settings2,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { format } from 'date-fns';
import { offlineDB } from '../utils/offlineDB';
import SearchableSelect from '../components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_BENCHMARKS = {
  truck:     { km_liter: 3.0,  liter_hr: null },
  excavator: { km_liter: null, liter_hr: 20.0 },
  other:     { km_liter: 8.0,  liter_hr: 12.0 },
};

const DieselConsumptionPage = ({ user }) => {
  const guard = useSubmitGuard();
  const [entries, setEntries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [efficiencyOpen, setEfficiencyOpen] = useState(false);
  const [efficiencyMetricFilter, setEfficiencyMetricFilter] = useState('all');
  const [benchmarkOpen, setBenchmarkOpen] = useState(false);
  const [benchmarks, setBenchmarks] = useState(() => {
    try {
      const saved = localStorage.getItem('lsn_diesel_benchmarks');
      return saved ? JSON.parse(saved) : DEFAULT_BENCHMARKS;
    } catch { return DEFAULT_BENCHMARKS; }
  });
  const [benchmarkDraft, setBenchmarkDraft] = useState(benchmarks);

  const saveBenchmarks = () => {
    setBenchmarks(benchmarkDraft);
    localStorage.setItem('lsn_diesel_benchmarks', JSON.stringify(benchmarkDraft));
    setBenchmarkOpen(false);
    toast.success('Benchmarks saved');
  };

  const getBenchmarkComparison = (asset) => {
    const b = benchmarks[asset.asset_type] || benchmarks.other;
    // km/L: higher is better
    if (asset.km_per_liter != null && b?.km_liter) {
      const pct = ((asset.km_per_liter - b.km_liter) / b.km_liter) * 100;
      return { metric: 'km/L', actual: asset.km_per_liter, target: b.km_liter, pct, good: pct >= 0 };
    }
    // L/hr: lower is better
    if (asset.liters_per_hour != null && b?.liter_hr) {
      const pct = ((b.liter_hr - asset.liters_per_hour) / b.liter_hr) * 100;
      return { metric: 'L/hr', actual: asset.liters_per_hour, target: b.liter_hr, pct, good: pct >= 0 };
    }
    return null;
  };
  
  const [isAddEntryOpen, setIsAddEntryOpen] = useState(false);
  const [isEditEntryOpen, setIsEditEntryOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteRequests, setDeleteRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);

  // Delete request dialog state
  const [isDeleteRequestOpen, setIsDeleteRequestOpen] = useState(false);
  const [deleteRequestEntry, setDeleteRequestEntry] = useState(null);
  const [deleteRequestReason, setDeleteRequestReason] = useState('');
  const [deleteRequestImage, setDeleteRequestImage] = useState(null);
  const [deleteRequestImagePreview, setDeleteRequestImagePreview] = useState(null);
  const [deletingRequest, setDeletingRequest] = useState(false);
  const imageInputRef = useRef(null);

  const fetchDeleteRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const [reqRes, countRes] = await Promise.all([
        axios.get(`${API}/delete-requests?status=pending&module=diesel`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/delete-requests/pending-count`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);
      setDeleteRequests(reqRes.data);
      setPendingCount(countRes.data.count || 0);
    } catch (_e) { /* silent */ }
  };
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [dateRange, setDateRange] = useState({
    start_date: format(new Date(new Date().setDate(1)), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd')
  });
  
  const [entryForm, setEntryForm] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    asset_door_no: '',
    fuel_quantity_liters: '',
    odometer_or_hour_meter: '',
    km_reading: '',
    notes: ''
  });

  // Determine if the selected asset is a truck (needs KM reading)
  const assetList = Array.isArray(assets) ? assets : [];
  const selectedAsset = assetList.find(a => a.door_no === entryForm.asset_door_no);
  const isTruck = !!(selectedAsset && ['Trucks', 'LMV-Trucks'].includes(
    selectedAsset.vehicle_category || selectedAsset.asset_type || ''
  ));

  useEffect(() => {
    fetchAssets();
  }, []);

  // Auto-open add entry dialog from sidebar quick link
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.search.includes('create=1')) {
      setIsAddEntryOpen(true);
      navigate('/diesel', { replace: true });
    }
  }, [location.search]);

  useEffect(() => {
    fetchData();
    if (canApprove) fetchDeleteRequests();
    window.addEventListener('offlineSyncComplete', fetchData);
    return () => window.removeEventListener('offlineSyncComplete', fetchData);
  }, [dateRange]);

  const fetchAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to fetch assets');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [entriesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/diesel/entries?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/diesel/analytics?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setEntries(entriesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to fetch diesel data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();

    if (!navigator.onLine) {
      try {
        await offlineDB.add({
          module: 'diesel',
          displayName: `Diesel – ${entryForm.asset_door_no || 'Unknown'} (${entryForm.date})`,
          endpoint: '/diesel/entries',
          method: 'POST',
          data: {
            ...entryForm,
            km_reading: entryForm.km_reading !== '' ? parseFloat(entryForm.km_reading) : null,
            odometer_or_hour_meter: entryForm.odometer_or_hour_meter !== '' ? parseFloat(entryForm.odometer_or_hour_meter) : null,
          },
        });
        const count = await offlineDB.pendingCount();
        if (window.__setOfflinePendingCount) window.__setOfflinePendingCount(count);
        toast.success('Saved locally — will sync when back online', {
          description: 'Diesel entry stored on this device',
          duration: 4000,
        });
        setIsAddEntryOpen(false);
        resetEntryForm();
      } catch (_e) {
        toast.error('Failed to save offline. Storage may be full.');
      }
      return;
    }

    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/diesel/entries`, {
          ...entryForm,
          km_reading: entryForm.km_reading !== '' ? parseFloat(entryForm.km_reading) : null,
          odometer_or_hour_meter: entryForm.odometer_or_hour_meter !== '' ? parseFloat(entryForm.odometer_or_hour_meter) : null,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Diesel entry added!');
        setIsAddEntryOpen(false);
        resetEntryForm();
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to add entry');
      }
    });
  };

  const canApprove = user?.is_admin || user?.is_super_admin || user?.permissions?.can_approve_deletions === true;

  const handleDeleteEntry = async (entry) => {
    if (canApprove) {
      // Admins/approvers: direct delete
      if (!window.confirm('Delete this diesel entry?')) return;
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/diesel/entries/${entry.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Entry deleted');
        fetchData();
        fetchDeleteRequests();
      } catch (error) {
        toast.error('Failed to delete entry');
      }
    } else {
      // Regular users: open the delete request dialog
      setDeleteRequestEntry(entry);
      setDeleteRequestReason('');
      setDeleteRequestImage(null);
      setDeleteRequestImagePreview(null);
      setIsDeleteRequestOpen(true);
    }
  };

  const handleDeleteRequestImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5 MB');
      return;
    }
    setDeleteRequestImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setDeleteRequestImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submitDeleteRequest = async () => {
    if (!deleteRequestEntry) return;
    setDeletingRequest(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('module', 'diesel');
      formData.append('record_id', deleteRequestEntry.id);
      formData.append('record_display', `Diesel – ${deleteRequestEntry.asset_door_no} on ${deleteRequestEntry.date} (${deleteRequestEntry.fuel_quantity_liters}L)`);
      formData.append('reason', deleteRequestReason);
      if (deleteRequestImage) formData.append('proof_image', deleteRequestImage);
      await axios.post(`${API}/delete-requests`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Delete request submitted for approval', {
        description: 'An admin will review your request',
        duration: 4000,
      });
      setIsDeleteRequestOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setDeletingRequest(false);
    }
  };

  const handleApproveDelete = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deletion approved and executed');
      fetchData();
      fetchDeleteRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectDelete = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/reject`, { note: 'Rejected' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Delete request rejected');
      fetchDeleteRequests();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setEntryForm({
      date: entry.date,
      asset_door_no: entry.asset_door_no,
      fuel_quantity_liters: String(entry.fuel_quantity_liters),
      odometer_or_hour_meter: entry.odometer_or_hour_meter != null ? String(entry.odometer_or_hour_meter) : '',
      km_reading: entry.km_reading != null ? String(entry.km_reading) : '',
      notes: entry.notes || '',
    });
    setIsEditEntryOpen(true);
  };

  const handleUpdateEntry = async (e) => {
    e.preventDefault();
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/diesel/entries/${editingEntry.id}`, {
          ...entryForm,
          km_reading: entryForm.km_reading !== '' ? parseFloat(entryForm.km_reading) : null,
          odometer_or_hour_meter: entryForm.odometer_or_hour_meter !== '' ? parseFloat(entryForm.odometer_or_hour_meter) : null,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Diesel entry updated');
        setIsEditEntryOpen(false);
        setEditingEntry(null);
        resetEntryForm();
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update entry');
      }
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/diesel/bulk-import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`Imported ${response.data.imported} entries!`);
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} rows had errors`);
      }
      setIsImportOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const resetEntryForm = () => {
    setEntryForm({
      date: format(new Date(), 'yyyy-MM-dd'),
      asset_door_no: '',
      fuel_quantity_liters: '',
      odometer_or_hour_meter: '',
      km_reading: '',
      notes: ''
    });
  };

  const getEfficiencyIcon = (rating) => {
    switch (rating) {
      case 'efficient': return <TrendingDown className="h-4 w-4 text-green-600" />;
      case 'high_consumption': return <TrendingUp className="h-4 w-4 text-red-600" />;
      default: return <Minus className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getEfficiencyColor = (rating) => {
    switch (rating) {
      case 'efficient': return 'bg-green-100 text-green-700';
      case 'high_consumption': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            Diesel Consumption
            {canApprove && pendingCount > 0 && (
              <span
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-600 text-white animate-pulse"
                data-testid="pending-delete-badge"
              >
                <Clock className="h-3 w-3" /> {pendingCount} Pending
              </span>
            )}
          </h1>
          <p className="text-slate-600 mt-1">Track fuel consumption and analyze efficiency</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIsImportOpen(true)} variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Import Excel
          </Button>
          <Button onClick={() => setIsAddEntryOpen(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-2" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-end gap-4">
          <div>
            <Label>From Date</Label>
            <Input
              type="date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>To Date</Label>
            <Input
              type="date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Fuel className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Fuel Consumed</p>
                <p className="text-2xl font-bold">{analytics.totals?.fuel_liters?.toLocaleString()} L</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Production Volume</p>
                <p className="text-2xl font-bold">{analytics.totals?.cum_volume?.toLocaleString()} m³</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Avg. Consumption</p>
                <p className="text-2xl font-bold">{analytics.totals?.avg_liters_per_cum?.toFixed(3)} L/m³</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Efficiency Analysis — collapsible */}
      {analytics?.by_asset && analytics.by_asset.length > 0 && (
        <div className="bg-white rounded-lg border shadow-sm">
          {/* Header row: toggle + benchmarks button */}
          <div className="flex items-center">
            <button
              type="button"
              className="flex-1 p-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors rounded-tl-lg rounded-tr-lg"
              onClick={() => setEfficiencyOpen(o => !o)}
              data-testid="efficiency-table-toggle"
            >
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Efficiency Analysis by Asset
                  <span className="ml-2 text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                    {analytics.by_asset.length} assets
                  </span>
                </h3>
                {!efficiencyOpen && (
                  <p className="text-xs text-slate-400 mt-0.5">Click to expand · km/L shown for all assets with odometer data · L/hr shown for all with hour-meter data</p>
                )}
              </div>
              <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${efficiencyOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className="pr-4 pl-2">
              <Button
                variant="outline"
                size="sm"
                className="text-xs gap-1.5 border-slate-300"
                onClick={(e) => { e.stopPropagation(); setBenchmarkDraft(benchmarks); setBenchmarkOpen(true); }}
                data-testid="set-benchmarks-btn"
              >
                <Target className="h-3.5 w-3.5 text-indigo-600" />
                Set Benchmarks
              </Button>
            </div>
          </div>

          {efficiencyOpen && (
            <div className="border-t overflow-x-auto">
              {/* Metric filter */}
              <div className="flex gap-2 px-4 py-3 bg-slate-50 border-b">
                <span className="text-xs text-slate-500 font-medium mr-1 self-center">Filter:</span>
                {[
                  { key: 'all', label: 'All Assets' },
                  { key: 'km', label: 'km/L only' },
                  { key: 'lhr', label: 'L/hr only' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setEfficiencyMetricFilter(opt.key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${efficiencyMetricFilter === opt.key ? 'bg-slate-900 text-white' : 'bg-white border text-slate-600 hover:bg-slate-100'}`}
                    data-testid={`metric-filter-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs">#</TableHead>
                    <TableHead className="text-xs">Asset</TableHead>
                    <TableHead className="text-xs text-center">Type</TableHead>
                    <TableHead className="text-right text-xs">Fuel (L)</TableHead>
                    <TableHead className="text-right text-xs">Volume (m³)</TableHead>
                    <TableHead className="text-right text-xs">Trips</TableHead>
                    <TableHead className="text-right text-xs">L/m³</TableHead>
                    <TableHead className="text-right text-xs">
                      <span className="flex items-center justify-end gap-1"><Truck className="h-3 w-3" /> km/L</span>
                    </TableHead>
                    <TableHead className="text-right text-xs">
                      <span className="flex items-center justify-end gap-1"><Gauge className="h-3 w-3" /> L/hr</span>
                    </TableHead>
                    <TableHead className="text-right text-xs">
                      <span className="flex items-center justify-end gap-1"><Target className="h-3 w-3 text-indigo-500" /> vs Target</span>
                    </TableHead>
                    <TableHead className="text-xs">Rating</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analytics.by_asset
                    .filter(asset => {
                      if (efficiencyMetricFilter === 'km') return asset.km_per_liter != null;
                      if (efficiencyMetricFilter === 'lhr') return asset.liters_per_hour != null;
                      return true;
                    })
                    .map((asset, idx) => (
                    <TableRow key={asset.door_no} className="hover:bg-slate-50">
                      <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                      <TableCell className="font-medium text-sm">{asset.door_no}</TableCell>
                      <TableCell className="text-center">
                        {asset.asset_type === 'truck' ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700"><Truck className="h-3 w-3" /> Truck</span>
                        ) : asset.asset_type === 'excavator' ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-orange-50 text-orange-700"><Gauge className="h-3 w-3" /> Excavator</span>
                        ) : asset.asset_type ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-600">{asset.asset_type}</span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm">{asset.fuel_liters?.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-sm">{asset.cum_volume > 0 ? asset.cum_volume.toLocaleString() : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="text-right text-sm">{asset.trips > 0 ? asset.trips : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="text-right font-medium text-sm">
                        {asset.liters_per_cum > 0 ? (
                          <span className={asset.liters_per_cum < 1.5 ? 'text-green-600' : asset.liters_per_cum < 2.5 ? 'text-amber-600' : 'text-red-600'}>
                            {asset.liters_per_cum.toFixed(3)}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-medium text-sm">
                        {asset.km_per_liter != null ? (
                          <span className={asset.km_per_liter >= 3 ? 'text-green-600' : asset.km_per_liter >= 1.5 ? 'text-amber-600' : 'text-red-600'}>
                            {asset.km_per_liter.toFixed(2)}
                          </span>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-medium text-sm">
                        {asset.liters_per_hour != null ? (
                          <span className={asset.liters_per_hour <= 15 ? 'text-green-600' : asset.liters_per_hour <= 25 ? 'text-amber-600' : 'text-red-600'}>
                            {asset.liters_per_hour.toFixed(2)}
                          </span>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {(() => {
                          const cmp = getBenchmarkComparison(asset);
                          if (!cmp) return <span className="text-slate-300 text-xs">—</span>;
                          const absPct = Math.abs(cmp.pct).toFixed(1);
                          return (
                            <span className={`inline-flex items-center gap-0.5 font-semibold ${cmp.good ? 'text-green-700' : 'text-red-600'}`}
                              title={`Target ${cmp.metric}: ${cmp.target} · Actual: ${cmp.actual?.toFixed(2)}`}
                            >
                              {cmp.good ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                              {absPct}%
                              <span className="text-[10px] font-normal opacity-70 ml-0.5">{cmp.metric}</span>
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getEfficiencyColor(asset.efficiency_rating)}`}>
                          {getEfficiencyIcon(asset.efficiency_rating)}
                          {asset.efficiency_rating === 'efficient' ? 'Efficient' :
                           asset.efficiency_rating === 'high_consumption' ? 'High' : 'Avg'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Pending Delete Requests — visible to approvers only */}
      {canApprove && deleteRequests.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2" data-testid="pending-delete-requests">
          <div className="flex items-center gap-2 mb-3">
            <Trash2 className="h-4 w-4 text-red-600" />
            <h3 className="font-semibold text-red-800 text-sm">
              Pending Deletion Requests ({deleteRequests.length})
            </h3>
          </div>
          {deleteRequests.map(req => (
            <div key={req.id} className="flex items-center justify-between bg-white rounded border border-red-100 px-3 py-2 gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{req.record_display}</p>
                <p className="text-xs text-slate-500">Requested by {req.requested_by_name} · {req.created_at ? new Date(req.created_at).toLocaleDateString('en-IN') : ''}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" className="h-7 text-xs text-green-700 border-green-300 hover:bg-green-50" onClick={() => handleApproveDelete(req.id)} data-testid={`approve-delete-${req.id}`}>Approve</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50" onClick={() => handleRejectDelete(req.id)} data-testid={`reject-delete-${req.id}`}>Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Entries */}
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="p-4 border-b">
          <h3 className="font-semibold">Recent Fuel Entries</h3>
        </div>
        {entries.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Fuel className="h-12 w-12 mx-auto mb-4 text-slate-400" />
            No fuel entries for this period
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Asset (Door No.)</TableHead>
                <TableHead className="text-right">Fuel (L)</TableHead>
                <TableHead className="text-right">Hour Meter</TableHead>
                <TableHead className="text-right">KM Reading</TableHead>
                <TableHead>Notes</TableHead>
                {(user?.is_admin || user?.is_super_admin || user?.permissions?.diesel?.edit) && (
                  <TableHead className="w-24">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.slice(0, 50).map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.date}</TableCell>
                  <TableCell className="font-medium">{entry.asset_door_no}</TableCell>
                  <TableCell className="text-right">{entry.fuel_quantity_liters}</TableCell>
                  <TableCell className="text-right">{entry.odometer_or_hour_meter || '-'}</TableCell>
                  <TableCell className="text-right">{entry.km_reading != null ? entry.km_reading.toLocaleString() : '-'}</TableCell>
                  <TableCell className="max-w-xs truncate text-sm text-slate-600">
                    {entry.notes || '-'}
                  </TableCell>
                        {(user?.is_admin || user?.is_super_admin || user?.permissions?.diesel?.edit) && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-slate-600 hover:text-blue-600"
                                onClick={() => openEditEntry(entry)}
                                data-testid={`edit-diesel-${entry.id}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                onClick={() => handleDeleteEntry(entry)}
                                title={canApprove ? 'Delete entry' : 'Request deletion (requires approval)'}
                                data-testid={`delete-diesel-${entry.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add Entry Modal */}
      <Dialog open={isAddEntryOpen} onOpenChange={setIsAddEntryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Diesel Entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddEntry} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={entryForm.date}
                  onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })}
                  required
                  className="mt-1.5"
                  min={!user?.is_super_admin ? format(new Date(Date.now() - 86400000), 'yyyy-MM-dd') : undefined}
                  max={!user?.is_super_admin ? format(new Date(), 'yyyy-MM-dd') : undefined}
                />
                {!user?.is_super_admin && <p className="text-xs text-amber-600 mt-1">Today or yesterday's date allowed</p>}
              </div>
              <div>
                <Label>Asset (Door No.) *</Label>
                <SearchableSelect
                  value={entryForm.asset_door_no}
                  onValueChange={(val) => setEntryForm({ ...entryForm, asset_door_no: val })}
                  options={assetList.map(a => ({ value: a.door_no, label: `${a.door_no} - ${a.asset_type}` }))}
                  placeholder="Select asset"
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fuel Quantity (Liters) *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={entryForm.fuel_quantity_liters}
                  onChange={(e) => setEntryForm({ ...entryForm, fuel_quantity_liters: e.target.value })}
                  required
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Hour Meter Reading</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={entryForm.odometer_or_hour_meter}
                  onChange={(e) => setEntryForm({ ...entryForm, odometer_or_hour_meter: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            {isTruck && (
              <div>
                <Label>KM Reading (Odometer) <span className="text-blue-600 text-xs ml-1">— Truck</span></Label>
                <Input
                  type="number"
                  step="1"
                  value={entryForm.km_reading}
                  onChange={(e) => setEntryForm({ ...entryForm, km_reading: e.target.value })}
                  placeholder="Enter current odometer reading..."
                  className="mt-1.5"
                  data-testid="km-reading-input"
                />
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Input
                value={entryForm.notes}
                onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })}
                className="mt-1.5"
                placeholder="Optional notes..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsAddEntryOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Add Entry</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Diesel Data</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Upload Excel/CSV with columns:</p>
            <ul className="text-sm text-slate-500 list-disc list-inside">
              <li>date</li>
              <li>asset_door_no (or door_no)</li>
              <li>fuel_quantity_liters (or fuel)</li>
              <li>odometer_or_hour_meter (or hour_meter) - optional</li>
              <li>notes - optional</li>
            </ul>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload} disabled={uploading} className="hidden" id="diesel-file" />
              <label htmlFor="diesel-file" className="cursor-pointer">
                {uploading ? <Loader2 className="h-8 w-8 mx-auto text-slate-400 animate-spin" /> : <Upload className="h-8 w-8 mx-auto text-slate-400" />}
                <p className="mt-2 text-sm text-slate-600">{uploading ? 'Uploading...' : 'Click to upload'}</p>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Diesel Entry Modal */}
      <Dialog open={isEditEntryOpen} onOpenChange={(open) => { if (!open) { setIsEditEntryOpen(false); setEditingEntry(null); resetEntryForm(); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Diesel Entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateEntry} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={entryForm.date} onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })} required className="mt-1.5" />
              </div>
              <div>
                <Label>Asset (Door No.) *</Label>
                <SearchableSelect
                  value={entryForm.asset_door_no}
                  onValueChange={(val) => setEntryForm({ ...entryForm, asset_door_no: val })}
                  options={assetList.map(a => ({ value: a.door_no, label: `${a.door_no} — ${a.make} ${a.model}` }))}
                  placeholder="Select asset"
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fuel Quantity (Liters) *</Label>
                <Input type="number" step="0.1" value={entryForm.fuel_quantity_liters} onChange={(e) => setEntryForm({ ...entryForm, fuel_quantity_liters: e.target.value })} required className="mt-1.5" />
              </div>
              <div>
                <Label>Hour Meter Reading</Label>
                <Input type="number" step="0.1" value={entryForm.odometer_or_hour_meter} onChange={(e) => setEntryForm({ ...entryForm, odometer_or_hour_meter: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            {isTruck && (
              <div>
                <Label>KM Reading (Odometer) <span className="text-blue-600 text-xs ml-1">— Truck</span></Label>
                <Input type="number" step="1" value={entryForm.km_reading} onChange={(e) => setEntryForm({ ...entryForm, km_reading: e.target.value })} placeholder="Enter current odometer reading..." className="mt-1.5" />
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Input value={entryForm.notes} onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })} className="mt-1.5" placeholder="Optional notes..." />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { setIsEditEntryOpen(false); setEditingEntry(null); resetEntryForm(); }}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Request Dialog — for non-admin users */}
      <Dialog open={isDeleteRequestOpen} onOpenChange={(open) => {
        if (!open) {
          setIsDeleteRequestOpen(false);
          setDeleteRequestEntry(null);
          setDeleteRequestReason('');
          setDeleteRequestImage(null);
          setDeleteRequestImagePreview(null);
          if (imageInputRef.current) imageInputRef.current.value = '';
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Request Entry Deletion
            </DialogTitle>
          </DialogHeader>
          {deleteRequestEntry && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <p className="font-semibold">{deleteRequestEntry.asset_door_no}</p>
                <p>{deleteRequestEntry.date} &mdash; {deleteRequestEntry.fuel_quantity_liters}L</p>
                {deleteRequestEntry.notes && <p className="text-xs text-amber-600 mt-1">{deleteRequestEntry.notes}</p>}
              </div>
              <div>
                <Label>Reason for Deletion <span className="text-red-500">*</span></Label>
                <Textarea
                  className="mt-1.5 resize-none"
                  rows={3}
                  placeholder="Explain why this entry should be deleted (e.g. duplicate entry, wrong asset, data error)..."
                  value={deleteRequestReason}
                  onChange={(e) => setDeleteRequestReason(e.target.value)}
                  data-testid="delete-reason-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1.5">
                  <ImagePlus className="h-4 w-4 text-slate-500" />
                  Proof Image <span className="text-slate-400 text-xs ml-1">(Optional — photo of paperwork, screenshot, etc.)</span>
                </Label>
                <div
                  className="mt-1.5 border-2 border-dashed border-slate-200 rounded-lg p-4 text-center cursor-pointer hover:border-slate-400 transition-colors"
                  onClick={() => imageInputRef.current?.click()}
                >
                  {deleteRequestImagePreview ? (
                    <div className="relative">
                      <img src={deleteRequestImagePreview} alt="Preview" className="max-h-32 mx-auto rounded object-contain" />
                      <button
                        type="button"
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteRequestImage(null);
                          setDeleteRequestImagePreview(null);
                          if (imageInputRef.current) imageInputRef.current.value = '';
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <ImagePlus className="h-8 w-8 mx-auto text-slate-300 mb-1" />
                      <p className="text-xs text-slate-500">Click to attach proof image</p>
                    </div>
                  )}
                </div>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleDeleteRequestImageChange}
                  data-testid="delete-proof-image-input"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsDeleteRequestOpen(false)} disabled={deletingRequest}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={submitDeleteRequest}
              disabled={deletingRequest || !deleteRequestReason.trim()}
              data-testid="submit-delete-request-btn"
            >
              {deletingRequest ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Benchmarks Modal */}
      <Dialog open={benchmarkOpen} onOpenChange={setBenchmarkOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-indigo-600" />
              Set Efficiency Benchmarks
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1 mb-3">
            Set target efficiency values per asset type. The "vs Target" column will compare each asset's actual performance against these benchmarks.
          </p>
          <div className="space-y-5">
            {[
              { type: 'truck', label: 'Truck / Tipper / Water Tanker', showKm: true, showHr: false },
              { type: 'excavator', label: 'Excavator / Dozer / Grader', showKm: false, showHr: true },
              { type: 'other', label: 'Other (LMV, Crane, etc.)', showKm: true, showHr: true },
            ].map(({ type, label, showKm, showHr }) => (
              <div key={type} className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">{label}</p>
                <div className="grid grid-cols-2 gap-3">
                  {showKm && (
                    <div>
                      <Label className="text-xs text-slate-500">Target km/L</Label>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          className="h-8 text-sm"
                          placeholder="e.g. 3.0"
                          value={benchmarkDraft[type]?.km_liter ?? ''}
                          onChange={e => setBenchmarkDraft(b => ({
                            ...b,
                            [type]: { ...b[type], km_liter: e.target.value === '' ? null : parseFloat(e.target.value) }
                          }))}
                          data-testid={`benchmark-${type}-km`}
                        />
                        <span className="text-xs text-slate-400 whitespace-nowrap">km/L</span>
                      </div>
                    </div>
                  )}
                  {showHr && (
                    <div>
                      <Label className="text-xs text-slate-500">Target L/hr</Label>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Input
                          type="number"
                          min="0"
                          step="0.5"
                          className="h-8 text-sm"
                          placeholder="e.g. 20.0"
                          value={benchmarkDraft[type]?.liter_hr ?? ''}
                          onChange={e => setBenchmarkDraft(b => ({
                            ...b,
                            [type]: { ...b[type], liter_hr: e.target.value === '' ? null : parseFloat(e.target.value) }
                          }))}
                          data-testid={`benchmark-${type}-lhr`}
                        />
                        <span className="text-xs text-slate-400 whitespace-nowrap">L/hr</span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5">
                  {showKm && 'km/L: higher is better (green = above target). '}
                  {showHr && 'L/hr: lower is better (green = below target).'}
                </p>
              </div>
            ))}
          </div>
          <div className="flex justify-between pt-2">
            <Button variant="ghost" size="sm" className="text-xs text-slate-500"
              onClick={() => setBenchmarkDraft(DEFAULT_BENCHMARKS)}
              data-testid="reset-benchmarks-btn">
              Reset to defaults
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setBenchmarkOpen(false)}>Cancel</Button>
              <Button size="sm" className="bg-indigo-700 hover:bg-indigo-800" onClick={saveBenchmarks}
                data-testid="save-benchmarks-btn">
                Save Benchmarks
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DieselConsumptionPage;
